import { getSelectionOffsets, isAtLineStart, stripHtml } from './caretUtils';
import { focusEditable } from './focusUtils';

/**
 * Block indentation (Tab / Shift+Tab on text, bullets, numbers, checkboxes;
 * also whole quote callouts when block-selected).
 *
 * Stored as `props.indent` (0..MAX_INDENT) and rendered as a left margin on
 * the block's root element — visual nesting only, the tree stays flat
 * (matching how Notion markdown exports indent with leading spaces).
 */

export const MAX_INDENT = 6;
export const INDENT_STEP_PX = 24;

/**
 * react-contenteditable keeps a stale onKeyDown when only `props.indent`
 * changes (html is unchanged). Tab / Backspace then still see the old indent
 * — Shift+Tab appears dead, and a second Backspace never drops the marker.
 * This map is written synchronously so the next keystroke sees the new level.
 */
const pendingIndentById = new Map();

const storedIndentOf = (block) => {
  const raw = block?.props?.indent;
  return Number.isFinite(raw) && raw > 0 ? Math.min(raw, MAX_INDENT) : 0;
};

export const indentOf = (block) => {
  const stored = storedIndentOf(block);
  if (!block?.id || !pendingIndentById.has(block.id)) return stored;
  const pending = pendingIndentById.get(block.id);
  if (pending === stored) {
    pendingIndentById.delete(block.id);
    return stored;
  }
  return pending;
};

const writeIndent = (block, onUpdate, next) => {
  pendingIndentById.set(block.id, next);
  onUpdate(block.id, { props: { ...block.props, indent: next } });
};

/** Stamp indent before React re-renders (stale contenteditable handlers). */
export const rememberIndent = (blockId, next) => {
  pendingIndentById.set(blockId, next);
};

/** After outdent/Tab at line start, the next Backspace continues the sequence
 *  even if replaceCaret parked the caret at the end. */
const lineStartHoldUntil = new Map();

export const holdLineStart = (blockId) => {
  if (blockId) lineStartHoldUntil.set(blockId, Date.now() + 2500);
};

export const clearLineStartHold = (blockId) => {
  if (blockId) lineStartHoldUntil.delete(blockId);
};

const hasLineStartHold = (blockId) => {
  if (!blockId || !lineStartHoldUntil.has(blockId)) return false;
  if (Date.now() > lineStartHoldUntil.get(blockId)) {
    lineStartHoldUntil.delete(blockId);
    return false;
  }
  return true;
};

export const shouldHandleLineStartBackspace = (
  e,
  el,
  { isEmpty, blockId, content } = {}
) => {
  if (e.key !== 'Backspace') {
    clearLineStartHold(blockId);
    return false;
  }
  if (isEmpty || hasLineStartHold(blockId)) return true;
  if (isAtLineStart(el)) return true;
  const offsets = getSelectionOffsets(el);
  if (!offsets || offsets.start !== offsets.end) return false;
  const prefix = leadingListPrefixLength(content ?? el?.innerHTML);
  return prefix > 0 && offsets.start <= prefix;
};

/** Explicit restart (`props.start`) on a number block, or null to count from 1. */
export const numberedStartOf = (block) => {
  const raw = block?.props?.start;
  if (!Number.isFinite(raw)) return null;
  const n = Math.floor(raw);
  return n >= 1 ? n : null;
};

/**
 * `props.start` when the typed marker is not 1 (`3. ` → `{ start: 3 }`).
 * `1. ` stays implicit so the item can join a run already in progress.
 */
export const startPropFromTyped = (typed) => {
  const n = parseInt(String(typed).match(/^(\d+)\./)?.[1], 10);
  return Number.isFinite(n) && n > 1 ? { start: n } : {};
};

/**
 * 1-based index for a numbered list item among flat siblings.
 * Same-indent `number` predecessors count; deeper nested items are skipped;
 * a shallower indent or non-number breaks the run. `props.start` restarts
 * the count (so typing "3. " shows 3, and Enter continues 4, 5…).
 */
export const getNumberedListIndex = (siblings, index) => {
  if (!siblings?.length || index < 0 || index >= siblings.length) return 1;
  const indent = indentOf(siblings[index]);
  const ownStart = numberedStartOf(siblings[index]);
  if (ownStart != null) return ownStart;
  let n = 1;
  for (let i = index - 1; i >= 0; i -= 1) {
    const s = siblings[i];
    if (s.type !== 'number') break;
    const sIndent = indentOf(s);
    if (sIndent < indent) break;
    if (sIndent === indent) {
      const sStart = numberedStartOf(s);
      if (sStart != null) return sStart + n;
      n += 1;
    }
  }
  return n;
};

/** Inline style for a block's root element ({} at indent 0). */
export const indentStyle = (block) => {
  const indent = indentOf(block);
  return indent ? { marginLeft: indent * INDENT_STEP_PX } : {};
};

/**
 * Shared Tab / Shift+Tab handler. Returns true when the event was an indent
 * change (caller should stop further handling).
 */
export const handleIndentKeyDown = (e, block, onUpdate, el) => {
  if (e.key !== 'Tab') return false;
  e.preventDefault();
  e.stopPropagation();
  const current = indentOf(block);
  const next = e.shiftKey
    ? Math.max(0, current - 1)
    : Math.min(MAX_INDENT, current + 1);
  if (next !== current) {
    writeIndent(block, onUpdate, next);
    if (isAtLineStart(el)) {
      holdLineStart(block.id);
      focusEditable(block.id, { atStart: true });
    }
  }
  return true;
};

/** Props fragment carrying a block's indent into a newly chained sibling. */
export const inheritIndent = (block) => {
  const indent = indentOf(block);
  return indent ? { indent } : {};
};

const LIST_LINE_TYPES = new Set(['bullet', 'number', 'checkbox']);

/** Typed/pasted "- " / "1. " sitting in a paragraph (not a real list block). */
const LEADING_LIST_PREFIX_RE = /^(?:[-*](?:\s|&nbsp;|\u00a0)|\d+\.(?:\s|&nbsp;|\u00a0))/;

export const leadingListPrefixLength = (html) => {
  const text = stripHtml(html || '').replace(/\u00a0/g, ' ');
  const m = text.match(/^(?:[-*]\s+|\d+\.\s+)/);
  return m ? m[0].length : 0;
};

export const stripLeadingListPrefix = (html) => {
  const raw = String(html || '');
  const next = raw.replace(LEADING_LIST_PREFIX_RE, '');
  return next === raw ? null : next;
};

const isBlankContent = (html) =>
  stripHtml(html || '')
    .replace(/[\u200B\u00a0]/g, '')
    .replace(/&nbsp;/gi, '')
    .trim() === '';

/**
 * Word-processor Backspace at the start of a line (or on an empty line):
 *   1. If indented, drop one indent level (keep list type / paragraph).
 *   2. If a flush list item has text, convert to a paragraph.
 *   3. If a flush list item is empty, delete it (caller merges / drops it).
 * Returns 'outdent' | 'unlist' | 'delete' | null.
 */
export const applyLineStartBackspace = (block, onUpdate, content) => {
  const current = indentOf(block);
  if (current > 0) {
    writeIndent(block, onUpdate, current - 1);
    return 'outdent';
  }
  if (LIST_LINE_TYPES.has(block.type)) {
    pendingIndentById.delete(block.id);
    if (isBlankContent(content ?? block.content)) return 'delete';
    onUpdate(block.id, {
      type: 'text',
      content: content ?? block.content ?? '',
      props: {},
    });
    return 'unlist';
  }
  const stripped = stripLeadingListPrefix(content ?? block.content);
  if (stripped != null && (block.type === 'text' || block.type === 'heading')) {
    onUpdate(block.id, { content: stripped });
    return 'unlist';
  }
  return null;
};

/** Apply line-start Backspace and the matching focus / merge side effects. */
export const consumeListStartBackspace = (
  e,
  block,
  { content, onUpdate, onMergeWithPrevious }
) => {
  const action = applyLineStartBackspace(block, onUpdate, content);
  if (!action) return false;
  e.preventDefault();
  e.stopPropagation?.();
  if (action === 'outdent' || action === 'unlist') {
    holdLineStart(block.id);
    focusEditable(block.id, { atStart: true });
  } else if (action === 'delete') {
    if (!onMergeWithPrevious?.(block.id, '')) {
      onUpdate(block.id, { type: 'text', content: '', props: {} });
      focusEditable(block.id, { atStart: true });
    }
  }
  return true;
};
